import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, Phone, CalendarCheck, Clock, BadgeCheck } from "lucide-react";
import { CONTACT, IMG } from "@/data/site";
import { MegaMenu } from "./MegaMenu";
import { MobileDrawer } from "./MobileDrawer";

export const MenuLabel = ({ en, hi }) => (
  <span className="flex flex-col leading-tight"><span className="font-en">{en}</span><span className="text-[11px] font-medium text-muted2">{hi}</span></span>
);

const navCls = ({ isActive }) => `py-2 text-[15px] font-semibold transition-colors ${isActive ? "text-maroon" : "text-ink hover:text-maroon"}`;

const TopBar = () => (
  <div data-testid="top-bar" className="bg-maroon-ink text-white/85">
    <div className="container-x flex h-9 items-center justify-between text-xs">
      <p className="flex items-center gap-2 truncate"><BadgeCheck className="h-3.5 w-3.5 text-amber-300" /> {CONTACT.freeCheckupHi}<span className="font-en hidden text-white/60 sm:inline">· {CONTACT.freeCheckupEn}</span></p>
      <div className="hidden items-center gap-5 md:flex">
        <span className="font-en flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {CONTACT.timingsEn}</span>
        <a href={`tel:${CONTACT.phoneDial}`} className="font-en flex items-center gap-1.5 font-semibold text-white"><Phone className="h-3.5 w-3.5" /> {CONTACT.phoneDisplay}</a>
      </div>
    </div>
  </div>
);

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  useEffect(() => setOpen(false), [location.key, location.pathname, location.search, location.hash]);
  useEffect(() => {
    if (!open) return;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [open]);
  useEffect(() => {
    const f = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", f);
    return () => window.removeEventListener("scroll", f);
  }, []);

  return (
    <header data-testid="site-header" className="sticky top-0 z-50">
      <TopBar />
      <div className={`border-b transition-[background-color,box-shadow] duration-300 ${scrolled ? "border-line bg-white/90 shadow-sm backdrop-blur-xl" : "border-transparent bg-white"}`}>
        <div className="container-x flex h-[74px] items-center justify-between gap-4">
          <Link to="/" data-testid="header-logo-link" className="shrink-0"><img src={IMG.logo} alt="Medicity Hospital" className="h-11 w-auto md:h-[52px]" /></Link>
          <nav data-testid="desktop-nav" className="hidden items-center gap-7 lg:flex">
            <NavLink to="/" end className={navCls} data-testid="nav-home"><MenuLabel en="Home" hi="होम" /></NavLink>
            <MegaMenu dept="eye" />
            <MegaMenu dept="surgery" />
            <NavLink to="/doctors" className={navCls} data-testid="nav-doctors"><MenuLabel en="Doctors" hi="डॉक्टर" /></NavLink>
            <NavLink to="/contact" className={navCls} data-testid="nav-contact"><MenuLabel en="Contact" hi="संपर्क" /></NavLink>
          </nav>
          <div className="flex items-center gap-2">
            <a href={`tel:${CONTACT.phoneDial}`} data-testid="header-call-button" aria-label="Call" className="hidden h-11 w-11 items-center justify-center rounded-full border border-line text-maroon transition-colors hover:bg-maroon-tint sm:flex"><Phone className="h-4 w-4" /></a>
            <Link to="/#appointment" data-testid="header-book-button" className="btn btn-maroon hidden !py-2.5 sm:inline-flex"><CalendarCheck className="h-4 w-4" /> अपॉइंटमेंट बुक करें</Link>
            <button data-testid="mobile-menu-button" onClick={() => setOpen((value) => !value)} aria-label="Menu" aria-expanded={open} aria-controls="mobile-navigation" className="flex h-11 w-11 items-center justify-center rounded-full bg-maroon-tint text-maroon lg:hidden"><Menu className="h-5 w-5" /></button>
          </div>
        </div>
      </div>
      <MobileDrawer open={open} onClose={() => setOpen(false)} />
    </header>
  );
};
